import { Building2, Cpu, FlaskConical, Layers, MapPin, Users } from 'lucide-react';
import type { NavigateFunction } from 'react-router-dom';
import {
  listCohorts,
  listDispensers,
  listOrganizations,
  listParticipants,
  listSites,
  listTrials,
} from '@/api/platform/generated/sdk.gen';
import type { AsyncCommandProvider } from '@/components/command-palette';

export function createPlatformCommandProviders(navigate: NavigateFunction): AsyncCommandProvider[] {
  return [
    // Organizations
    {
      id: 'platform:organizations',
      kind: 'async',
      label: 'Organizations',
      icon: Building2,
      priority: 20,
      scopeKeyword: 'orgs',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/organizations',
      search: async (query, { signal }) => {
        const { data } = await listOrganizations({
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((org: any) => ({
          id: `platform:org:${org.id}`,
          label: org.name,
          subtitle: org.slug,
          icon: Building2,
          onSelect: () => navigate(`/organizations/${org.slug}`),
        }));
      },
    },

    // Trials
    {
      id: 'platform:trials',
      kind: 'async',
      label: 'Trials',
      icon: FlaskConical,
      priority: 21,
      scopeKeyword: 'trials',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/organizations',
      search: async (query, { signal }) => {
        // Trials are nested under organizations — search all orgs
        const { data: orgs } = await listOrganizations({
          query: { per_page: 50, page: 1 },
          signal,
          throwOnError: true,
        });

        // Search trials across all organizations (parallel)
        const trialPromises = (orgs?.items ?? []).map(async (org: any) => {
          try {
            const { data } = await listTrials({
              path: { org_id: org.id },
              query: { search: query, per_page: 3, page: 1 },
              signal,
              throwOnError: true,
            });
            return (data?.items ?? []).map((trial: any) => ({
              id: `platform:trial:${trial.id}`,
              label: trial.name,
              subtitle: org.name,
              icon: FlaskConical,
              onSelect: () => navigate(`/organizations/${org.slug}/trials/${trial.slug}`),
            }));
          } catch {
            return [];
          }
        });

        const trialResults = await Promise.all(trialPromises);
        return trialResults.flat().slice(0, 5);
      },
    },

    // Participants — context-aware (needs trial from URL)
    {
      id: 'platform:participants',
      kind: 'async',
      label: 'Participants',
      icon: Users,
      priority: 22,
      scopeKeyword: 'pt',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => {
        const m = window.location.pathname.match(/\/organizations\/([^/]+)\/trials\/([^/]+)/);
        return m ? `/organizations/${m[1]}/trials/${m[2]}?tab=participants` : '/organizations';
      },
      search: async (query, { signal }) => {
        // Extract trial context from the current URL
        const match = window.location.pathname.match(/\/organizations\/([^/]+)\/trials\/([^/]+)/);
        if (!match) return []; // Participants only searchable within trial context

        const [, orgSlug, trialSlug] = match;

        // Need trial_id from the URL slug — use the slug-based endpoint
        const { data } = await listParticipants({
          path: { trial_id: trialSlug },
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((p: any) => ({
          id: `platform:participant:${p.id}`,
          label: p.participant_id,
          subtitle: p.site_name || undefined,
          icon: Users,
          onSelect: () =>
            navigate(`/organizations/${orgSlug}/trials/${trialSlug}/participants/${p.id}`),
        }));
      },
    },

    // Sites — context-aware (needs trial from URL)
    {
      id: 'platform:sites',
      kind: 'async',
      label: 'Sites',
      icon: MapPin,
      priority: 23,
      scopeKeyword: 'sites',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => {
        const m = window.location.pathname.match(/\/organizations\/([^/]+)\/trials\/([^/]+)/);
        return m ? `/organizations/${m[1]}/trials/${m[2]}?tab=sites` : '/organizations';
      },
      search: async (query, { signal }) => {
        const match = window.location.pathname.match(/\/organizations\/([^/]+)\/trials\/([^/]+)/);
        if (!match) return [];

        const [, orgSlug, trialSlug] = match;

        const { data } = await listSites({
          path: { trial_id: trialSlug },
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((site: any) => ({
          id: `platform:site:${site.id}`,
          label: site.name,
          subtitle: site.address || undefined,
          icon: MapPin,
          onSelect: () => navigate(`/organizations/${orgSlug}/trials/${trialSlug}?tab=sites`),
        }));
      },
    },

    // Dispensers — top-level list, client-side filter by serial number
    {
      id: 'platform:dispensers',
      kind: 'async',
      label: 'Devices',
      icon: Cpu,
      priority: 24,
      scopeKeyword: 'devices',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/dispensers',
      search: async (query, { signal }) => {
        const { data } = await listDispensers({
          query: { per_page: 50, page: 1 },
          signal,
          throwOnError: true,
        });
        const q = query.toLowerCase();
        return (data?.items ?? [])
          .filter(
            (d: any) =>
              (d.serial_number || '').toLowerCase().includes(q) ||
              (d.device_name || '').toLowerCase().includes(q)
          )
          .slice(0, 5)
          .map((d: any) => ({
            id: `platform:dispenser:${d.id}`,
            label: d.serial_number || d.id,
            subtitle: d.trial_name || d.status || undefined,
            icon: Cpu,
            onSelect: () => navigate(`/dispensers/${d.id}`),
          }));
      },
    },

    // Cohorts — context-aware (needs trial from URL)
    {
      id: 'platform:cohorts',
      kind: 'async',
      label: 'Cohorts',
      icon: Layers,
      priority: 25,
      scopeKeyword: 'cohorts',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => {
        const m = window.location.pathname.match(/\/organizations\/([^/]+)\/trials\/([^/]+)/);
        return m ? `/organizations/${m[1]}/trials/${m[2]}?tab=cohorts` : '/organizations';
      },
      search: async (query, { signal }) => {
        const match = window.location.pathname.match(/\/organizations\/([^/]+)\/trials\/([^/]+)/);
        if (!match) return [];

        const [, orgSlug, trialSlug] = match;

        const { data } = await listCohorts({
          path: { trial_id: trialSlug },
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((c: any) => ({
          id: `platform:cohort:${c.id}`,
          label: c.name,
          subtitle: c.status || undefined,
          icon: Layers,
          onSelect: () => navigate(`/organizations/${orgSlug}/trials/${trialSlug}?tab=cohorts`),
        }));
      },
    },
  ];
}
